/**
 * useCursorSync — Real-time cursor position broadcasting via Firebase RTDB.
 *
 * Stores cursor positions in canvas (world) coordinates so they are
 * viewport-independent. Each viewer converts to screen coords locally.
 *
 * RTDB path: cursors/{projectId}/{userId}
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { getDatabase, ref, onValue, set, onDisconnect } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { keepList } from '../utils/unchanged';
// A cursor has its user's colour, the same one as their avatar
import { collaboratorColor } from './usePresence';

export interface RemoteCursor {
  userId: string;
  displayName: string;
  color: string;
  /** Canvas-space X */
  x: number;
  /** Canvas-space Y */
  y: number;
  lastUpdated: number;
}

const THROTTLE_MS = 60; // ~16 updates/sec max
const STALE_MS = 10_000; // Hide cursors not updated in 10 s

/** The other users' live cursors in what the database holds for a workspace (everyone's, this user's own included). */
export function otherCursors(data: unknown, ownUserId: string, now: number): RemoteCursor[] {
  if (!data || typeof data !== 'object') return [];
  return Object.values(data as Record<string, any>)
    .filter((c: any) => c && c.userId && c.userId !== ownUserId)
    .filter((c: any) => now - (c.lastUpdated || 0) < STALE_MS)
    .map((c: any) => ({
      userId: c.userId,
      displayName: c.displayName || 'Anonymous',
      color: c.color || collaboratorColor(c.userId),
      x: c.x ?? 0,
      y: c.y ?? 0,
      lastUpdated: c.lastUpdated || 0,
    }));
}

/** Whether a cursor would be drawn the same (when it was last sent does not show). */
const sameCursor = (a: RemoteCursor, b: RemoteCursor): boolean =>
  a.userId === b.userId && a.x === b.x && a.y === b.y && a.displayName === b.displayName && a.color === b.color;

/**
 * The list to keep in state after an update: the previous one when no other user's cursor
 * changed. This user's own writes come back through the listener too, so alone in a
 * workspace the list stays the same empty array and nothing renders.
 */
export const keepCursors = (previous: RemoteCursor[], next: RemoteCursor[]): RemoteCursor[] => keepList(previous, next, sameCursor);

/**
 * @param broadcast whether this user's cursor is sent. Off while nobody else is in the
 *   workspace: nothing is written, and what was written is taken back.
 */
export function useCursorSync(
  projectId: string | null,
  enabled: boolean = true,
  broadcast: boolean = true,
) {
  const [remoteCursors, setRemoteCursors] = useState<RemoteCursor[]>([]);
  const myRef = useRef<ReturnType<typeof ref> | null>(null);
  const lastWrite = useRef(0);
  const pendingWrite = useRef<ReturnType<typeof setTimeout> | null>(null);
  const broadcasting = useRef(broadcast);
  // Whether the database may hold a cursor of this user (written since the listener started)
  const written = useRef(false);

  // Broadcast local cursor position (canvas coords)
  const broadcastCursor = useCallback(
    (canvasX: number, canvasY: number) => {
      if (!myRef.current || !broadcasting.current) return;

      const now = Date.now();
      const elapsed = now - lastWrite.current;

      const doWrite = () => {
        pendingWrite.current = null;
        const auth = getAuth();
        const u = auth.currentUser;
        if (!u || !myRef.current || !broadcasting.current) return;
        lastWrite.current = Date.now();
        if (!written.current) {
          written.current = true;
          // Remove my cursor on disconnect
          onDisconnect(myRef.current).remove().catch(() => {});
        }
        set(myRef.current, {
          userId: u.uid,
          displayName: u.displayName || u.email || 'Anonymous',
          color: collaboratorColor(u.uid),
          x: Math.round(canvasX),
          y: Math.round(canvasY),
          lastUpdated: Date.now(),
        }).catch((err) => {
          console.warn('[CursorSync] Failed to write cursor:', err?.message || err);
        });
      };

      if (elapsed >= THROTTLE_MS) {
        if (pendingWrite.current) {
          clearTimeout(pendingWrite.current);
          pendingWrite.current = null;
        }
        doWrite();
      } else {
        // Schedule a trailing write so the final position is always sent
        if (pendingWrite.current) clearTimeout(pendingWrite.current);
        pendingWrite.current = setTimeout(doWrite, THROTTLE_MS - elapsed);
      }
    },
    [],
  );

  /** Nothing more is sent, and the cursor that was sent is taken out of the database. */
  const withdrawCursor = useCallback(() => {
    if (pendingWrite.current) {
      clearTimeout(pendingWrite.current);
      pendingWrite.current = null;
    }
    if (myRef.current && written.current) {
      written.current = false;
      set(myRef.current, null).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (!projectId || !enabled) {
      setRemoteCursors(previous => keepCursors(previous, []));
      return;
    }

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    let db: ReturnType<typeof getDatabase>;
    try {
      db = getDatabase();
    } catch {
      return;
    }

    const cursorsPath = `cursors/${projectId}`;
    const myPath = `${cursorsPath}/${user.uid}`;
    const cursorsRef = ref(db, cursorsPath);
    const myNodeRef = ref(db, myPath);
    myRef.current = myNodeRef;

    console.log('[CursorSync] Listening for cursors at', cursorsPath, 'as user', user.uid);

    // The cursors of the workspace opened before are not in this one
    setRemoteCursors(previous => keepCursors(previous, []));

    // Listen for all cursors in this project
    const stopListening = onValue(cursorsRef, (snapshot) => {
      const cursors = otherCursors(snapshot.val(), user.uid, Date.now());
      setRemoteCursors(previous => keepCursors(previous, cursors));
    });

    return () => {
      // (off() with what onValue returns removes nothing: the listener of every workspace left stayed on)
      stopListening();
      // Clean up cursor on unmount
      withdrawCursor();
      myRef.current = null;
    };
  }, [projectId, enabled, withdrawCursor]);

  // The last other user left (or the first one came): stop (or start) sending this user's cursor
  useEffect(() => {
    broadcasting.current = broadcast;
    if (!broadcast) withdrawCursor();
  }, [broadcast, withdrawCursor]);

  return { remoteCursors, broadcastCursor };
}
